import { useState, useEffect } from 'react';
import api from './api';

export default function StockTab({ user, premixes }) {
  const [summary, setSummary] = useState([]);
  const [stockIn, setStockIn] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [factoryArrivals, setFactoryArrivals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddStock, setShowAddStock] = useState(false);
  const [showAddShipment, setShowAddShipment] = useState(false);
  const [showAddFactoryArrival, setShowAddFactoryArrival] = useState(false);
  const [activeSection, setActiveSection] = useState('summary');

  const canStockIn = ['admin', 'warehouse'].includes(user.role);
  const canShip = ['admin', 'sales'].includes(user.role);
  const canFactoryArrival = ['admin', 'factory'].includes(user.role);

  const load = async () => {
    try {
      const [sumRes, stockRes, shipRes, factRes] = await Promise.all([
        api.get('/stock-summary'),
        api.get('/stock-in'),
        api.get('/shipments'),
        api.get('/factory-arrivals'),
      ]);
      setSummary(sumRes.data);
      setStockIn(stockRes.data);
      setShipments(shipRes.data);
      setFactoryArrivals(factRes.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) return <div style={s.loading}>加载中...</div>;

  return (
    <div>
      <div style={s.header}>
        <h2 style={s.title}>预混料库存</h2>
        <div style={s.btnGroup}>
          {canStockIn && <button style={s.btnPrimary} onClick={() => setShowAddStock(true)}>+ 自产入库</button>}
          {canShip && <button style={{ ...s.btnPrimary, background: '#0f766e' }} onClick={() => setShowAddShipment(true)}>+ 发货记录</button>}
          {canFactoryArrival && <button style={{ ...s.btnPrimary, background: '#7e22ce' }} onClick={() => setShowAddFactoryArrival(true)}>+ 代工厂到货</button>}
        </div>
      </div>

      {/* 库存卡片 */}
      <div style={s.cardRow}>
        {summary.map(pm => (
          <div key={pm.id} style={{ ...s.card, ...(pm.warning ? s.cardWarn : {}) }}>
            <div style={s.cardName}>{pm.name}</div>
            <div style={s.cardQty}>{pm.currentStock?.toFixed(0) ?? 0} <span style={s.unit}>kg</span></div>
            <div style={s.cardMeta}>
              <div style={s.metaRow}><span>累计入库</span><span style={{ color: '#34d399' }}>{pm.totalIn?.toFixed(0)} kg</span></div>
              <div style={s.metaRow}><span>已发代工厂</span><span style={{ color: '#f59e0b' }}>{pm.totalShipped?.toFixed(0)} kg</span></div>
              <div style={s.metaRow}><span>代工厂确认到货</span><span style={{ color: '#a78bfa' }}>{pm.factoryStock?.toFixed(0)} kg</span></div>
              <div style={s.metaRow}><span>实际消耗</span><span style={{ color: '#94a3b8' }}>{pm.actualUsed?.toFixed(0)} kg</span></div>
              <div style={s.metaRow}><span>计划待用</span><span style={{ color: '#38bdf8' }}>{pm.plannedPending?.toFixed(0)} kg</span></div>
            </div>
            {pm.warning && <div style={s.warnBadge}>⚠ 库存不足计划需求</div>}
          </div>
        ))}
      </div>

      {/* 分段记录 */}
      <div style={s.tabs}>
        {[['summary', '入库记录'], ['shipments', '发货记录'], ['factory', '代工厂到货']].map(([key, label]) => (
          <button key={key} style={{ ...s.tabBtn, ...(activeSection === key ? s.tabBtnActive : {}) }}
            onClick={() => setActiveSection(key)}>{label}</button>
        ))}
      </div>

      {activeSection === 'summary' && (
        <RecordTable
          data={stockIn.map(e => ({ date: e.date, name: e.premix_name, qty: e.qty_kg, note: e.note, by: e.created_by_name }))}
          cols={['日期', '预混料', '入库量(kg)', '备注', '录入人']} />
      )}
      {activeSection === 'shipments' && (
        <RecordTable
          data={shipments.map(e => ({ date: e.ship_date, name: e.premix_name, qty: e.qty_kg, note: e.note, by: e.created_by_name }))}
          cols={['发货日期', '预混料', '发货量(kg)', '备注', '录入人']} />
      )}
      {activeSection === 'factory' && (
        <RecordTable
          data={factoryArrivals.map(e => ({ date: e.arrival_date, name: e.premix_name, qty: e.qty_kg, note: e.note, by: '代工厂' }))}
          cols={['到货日期', '预混料', '到货量(kg)', '备注', '录入人']} />
      )}

      {showAddStock && (
        <EntryModal title="自产入库" premixes={premixes} dateLabel="入库日期"
          onClose={() => setShowAddStock(false)}
          onSave={async d => { await api.post('/stock-in', { premix_id: d.premixId, qty_kg: d.qty, date: d.date, note: d.note }); setShowAddStock(false); load(); }} />
      )}
      {showAddShipment && (
        <EntryModal title="发货给代工厂" premixes={premixes} dateLabel="发货日期"
          onClose={() => setShowAddShipment(false)}
          onSave={async d => { await api.post('/shipments', { premix_id: d.premixId, qty_kg: d.qty, ship_date: d.date, note: d.note }); setShowAddShipment(false); load(); }} />
      )}
      {showAddFactoryArrival && (
        <EntryModal title="代工厂确认到货" premixes={premixes} dateLabel="到货日期"
          onClose={() => setShowAddFactoryArrival(false)}
          onSave={async d => { await api.post('/factory-arrivals', { premix_id: d.premixId, qty_kg: d.qty, arrival_date: d.date, note: d.note }); setShowAddFactoryArrival(false); load(); }} />
      )}
    </div>
  );
}

function RecordTable({ data, cols }) {
  if (!data.length) return <div style={{ color: '#334155', padding: 24, textAlign: 'center' }}>暂无记录</div>;
  return (
    <div style={s.tableWrap}>
      <table style={s.table}>
        <thead><tr>{cols.map(c => <th key={c} style={s.th}>{c}</th>)}</tr></thead>
        <tbody>
          {[...data].sort((a, b) => b.date?.localeCompare(a.date)).map((row, i) => (
            <tr key={i} style={s.tr}>
              <td style={s.td}>{row.date?.slice(0, 10)}</td>
              <td style={s.td}>{row.name}</td>
              <td style={s.td}><span style={s.qty}>{Number(row.qty).toLocaleString()}</span></td>
              <td style={s.td}>{row.note || '—'}</td>
              <td style={s.td}>{row.by || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EntryModal({ title, premixes, dateLabel, onClose, onSave }) {
  const [form, setForm] = useState({ premixId: premixes[0]?.id, qty: '', date: new Date().toISOString().slice(0, 10), note: '' });
  const [saving, setSaving] = useState(false);
  const handleSave = async () => { setSaving(true); try { await onSave(form); } finally { setSaving(false); } };
  return (
    <div style={s.overlay}>
      <div style={s.modal}>
        <div style={s.modalHeader}>
          <span style={s.modalTitle}>{title}</span>
          <button style={s.closeBtn} onClick={onClose}>✕</button>
        </div>
        <div style={s.formGrid}>
          <label style={s.label}>预混料</label>
          <select style={s.input} value={form.premixId} onChange={e => setForm(p => ({ ...p, premixId: Number(e.target.value) }))}>
            {premixes.map(pm => <option key={pm.id} value={pm.id}>{pm.name}</option>)}
          </select>
          <label style={s.label}>{dateLabel}</label>
          <input style={s.input} type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} />
          <label style={s.label}>数量(kg)</label>
          <input style={s.input} type="number" value={form.qty} onChange={e => setForm(p => ({ ...p, qty: Number(e.target.value) }))} />
          <label style={s.label}>备注</label>
          <input style={s.input} value={form.note} onChange={e => setForm(p => ({ ...p, note: e.target.value }))} />
        </div>
        <div style={s.modalFooter}>
          <button style={s.btnSec} onClick={onClose}>取消</button>
          <button style={s.btnPrimary} onClick={handleSave} disabled={saving}>{saving ? '保存中...' : '保存'}</button>
        </div>
      </div>
    </div>
  );
}

const s = {
  loading: { color: '#475569', padding: 40, textAlign: 'center' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 10 },
  title: { fontSize: 18, fontWeight: 700, color: '#f1f5f9', margin: 0 },
  btnGroup: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  btnPrimary: { background: '#1d4ed8', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: 7, cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' },
  btnSec: { background: '#1e2d4a', color: '#94a3b8', border: '1px solid #2d3f5f', padding: '8px 18px', borderRadius: 7, cursor: 'pointer', fontSize: 13, fontFamily: 'inherit' },
  cardRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(220px,1fr))', gap: 16, marginBottom: 28 },
  card: { background: '#0d1529', border: '1px solid #1e2d4a', borderRadius: 12, padding: 20 },
  cardWarn: { border: '1px solid #92400e', background: '#1c1208' },
  cardName: { fontSize: 13, color: '#64748b', fontWeight: 600, marginBottom: 8 },
  cardQty: { fontSize: 30, fontWeight: 800, color: '#38bdf8', lineHeight: 1 },
  unit: { fontSize: 14, color: '#64748b' },
  cardMeta: { marginTop: 12, fontSize: 12, color: '#475569' },
  metaRow: { display: 'flex', justifyContent: 'space-between', padding: '3px 0', borderBottom: '1px solid #0f1e35' },
  warnBadge: { marginTop: 10, background: '#92400e22', color: '#fbbf24', border: '1px solid #92400e', borderRadius: 6, padding: '4px 10px', fontSize: 12 },
  tabs: { display: 'flex', gap: 4, marginBottom: 16 },
  tabBtn: { background: '#0d1529', color: '#64748b', border: '1px solid #1e2d4a', padding: '7px 16px', borderRadius: 7, cursor: 'pointer', fontSize: 13, fontFamily: 'inherit' },
  tabBtnActive: { background: '#1e3a5f', color: '#38bdf8', borderColor: '#2d5a8a' },
  tableWrap: { overflowX: 'auto', borderRadius: 10, border: '1px solid #1e2d4a' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { background: '#0d1529', color: '#64748b', padding: '10px 14px', textAlign: 'left', fontWeight: 600, fontSize: 12, letterSpacing: '0.06em', borderBottom: '1px solid #1e2d4a' },
  tr: { borderBottom: '1px solid #1a2540' },
  td: { padding: '11px 14px', color: '#cbd5e1' },
  qty: { color: '#34d399', fontWeight: 700 },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 },
  modal: { background: '#0d1529', border: '1px solid #1e2d4a', borderRadius: 14, width: 'min(480px,95vw)', maxHeight: '85vh', overflowY: 'auto' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 24px', borderBottom: '1px solid #1e2d4a' },
  modalTitle: { fontSize: 15, fontWeight: 700, color: '#f1f5f9' },
  closeBtn: { background: 'none', border: 'none', color: '#64748b', fontSize: 18, cursor: 'pointer' },
  modalFooter: { display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '16px 24px', borderTop: '1px solid #1e2d4a' },
  formGrid: { display: 'grid', gridTemplateColumns: '90px 1fr', gap: '10px 16px', padding: '20px 24px', alignItems: 'center' },
  label: { fontSize: 12, color: '#64748b', fontWeight: 600, textAlign: 'right' },
  input: { background: '#0a1628', border: '1px solid #2d3f5f', color: '#e2e8f0', padding: '8px 12px', borderRadius: 7, fontSize: 13, fontFamily: 'inherit', width: '100%', boxSizing: 'border-box' },
};
